"use client";

import Link from "next/link";
import { useEffect } from "react";

import {
	DATABASE_SCHEMA_OUT_OF_DATE_HINT,
	databaseConnectionUserMessage,
} from "@/lib/db-connection-user-message";

export default function PublicSiteError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	const dbMsg = databaseConnectionUserMessage(error);
	const title =
		dbMsg === DATABASE_SCHEMA_OUT_OF_DATE_HINT
			? "Database / client schema mismatch"
			: dbMsg
				? "Site temporarily unavailable"
				: "Something went wrong";

	useEffect(() => {
		if (process.env.NODE_ENV === "development" && !dbMsg) {
			console.error(error);
		}
	}, [error, dbMsg]);

	return (
		<div className="container mx-auto max-w-lg px-4 py-16 text-center">
			<h1 className="font-semibold text-2xl tracking-tight">{title}</h1>
			<p className="mt-2 text-muted-foreground text-sm">
				{dbMsg ?? "This page could not be loaded. Try again in a moment."}
			</p>
			{dbMsg ? null : (
				<p className="mt-3 break-words font-mono text-[11px] text-muted-foreground">
					{error.message}
				</p>
			)}
			<div className="mt-6 flex flex-wrap items-center justify-center gap-4">
				<button
					className="rounded-md bg-primary px-4 py-2 text-primary-foreground text-sm hover:bg-primary/90"
					onClick={reset}
					type="button"
				>
					Try again
				</button>
				<Link className="text-primary text-sm underline" href="/">
					Home
				</Link>
			</div>
			{dbMsg === DATABASE_SCHEMA_OUT_OF_DATE_HINT ? (
				<p className="mt-8 text-left text-muted-foreground text-xs">
					Restart the dev server afterwards — the old client stays cached in
					memory.
				</p>
			) : null}
		</div>
	);
}
